import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Wallet, Copy, CheckCircle2, RefreshCw, AlertTriangle, QrCode, Users, Download, History, Banknote, ShieldCheck, Clock } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CUSTODY = {
  aguardando: { t: "Aguardando pagamento — custódia não iniciada", c: "border-amber-500/30 bg-amber-500/10 text-amber-300", icon: <Clock className="h-4 w-4" /> },
  protegido: { t: "Pagamento protegido — valor em custódia até a liberação", c: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", icon: <ShieldCheck className="h-4 w-4" /> },
  liberado: { t: "Pagamento liberado aos músicos", c: "border-sky-500/30 bg-sky-500/10 text-sky-300", icon: <CheckCircle2 className="h-4 w-4" /> },
};

const PAY_LABEL = {
  pendente: { t: "Pendente", c: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  processando: { t: "Processando", c: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  pago: { t: "Pago", c: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};
const COLL_LABEL = {
  PENDING: "Aguardando pagamento", PROCESSING: "Aguardando pagamento", PAID: "Pagamento confirmado",
  EXPIRED: "Pix expirado", CANCELLED: "Cancelado", FAILED: "Falha no pagamento",
  REFUNDED: "Estornado", PARTIALLY_REFUNDED: "Estorno parcial",
};
const PAYOUT_PT = {
  NOT_ELIGIBLE: "Sem chave Pix", PENDING: "Aguardando show/repasse", AUTHORIZED: "Autorizado",
  PROCESSING: "Enviado (processando)", PAID: "Recebido", FAILED: "Falha no repasse", CANCELLED: "Cancelado",
  AGUARDANDO_SALDO: "Aguardando saldo", AGUARDANDO_AUTORIZACAO: "Aguardando autorização",
};

export default function BandPayments({ eventId }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const load = () => {
    api.get(`/events/${eventId}/band`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/events/${eventId}/payment-orders`).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [eventId]);

  const payable = useMemo(() => (data?.musicians || []).filter((m) => m.payment_status === "pendente"), [data]);
  const selectedIds = useMemo(() => Object.keys(sel).filter((k) => sel[k]), [sel]);
  const fee = data?.fee || { fee_type: "percentage", percentage: 0, fixed: 0 };
  const feePer = (cache) => fee.fee_type === "fixed" ? fee.fixed
    : fee.fee_type === "percentage_fixed" ? (cache * fee.percentage / 100 + fee.fixed) : (cache * fee.percentage / 100);
  const cacheOf = (id) => ((data?.musicians || []).find((m) => m.user_id === id)?.cache) || 0;
  const subtotal = selectedIds.reduce((a, id) => a + cacheOf(id), 0);
  const feeTotal = selectedIds.reduce((a, id) => a + feePer(cacheOf(id)), 0);
  const ex = data?.express;
  const expressSurcharge = ex?.active
    ? (ex.type === "percentage" ? subtotal * ex.percentage / 100
      : ex.type === "percentage_fixed" ? subtotal * ex.percentage / 100 + ex.fixed : ex.fixed)
    : 0;
  const total = subtotal + feeTotal + expressSurcharge;

  const toggleAll = () => {
    if (selectedIds.length === payable.length) return setSel({});
    const n = {}; payable.forEach((m) => { n[m.user_id] = true; }); setSel(n);
  };

  const releasePayment = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/events/${eventId}/release-payment`);
      toast.success(r.released ? `${r.released} repasse(s) liberado(s) aos músicos.` : "Nenhum repasse pendente para liberar.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const charge = async () => {
    if (selectedIds.length === 0) return toast.error("Selecione ao menos um músico.");
    setBusy(true);
    try {
      const { data: o } = await api.post(`/events/${eventId}/band-charge`, { musician_ids: selectedIds });
      setOrder(o); setSel({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Poll order status while dialog open and unpaid
  useEffect(() => {
    if (!order || ["PAID", "CANCELLED", "REFUNDED"].includes(order.collection_status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data: o } = await api.get(`/payment-orders/${order.id}`);
        setOrder((p) => p && { ...p, ...o });
        if (o.collection_status === "PAID") { toast.success("Pagamento confirmado!"); load(); }
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [order?.id, order?.collection_status]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const regenerate = async () => {
    try { const { data: o } = await api.post(`/payment-orders/${order.id}/regenerate`); setOrder((p) => ({ ...p, ...o })); toast.success("Novo Pix gerado."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Código Pix copiado!"); };

  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/payment-orders/${id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `recibo-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível gerar o recibo."); }
  };

  const runPayouts = async () => {
    try {
      await api.post(`/payment-orders/${order.id}/payout`);
      toast.success("Repasses iniciados via Pix.");
      const { data: o } = await api.get(`/payment-orders/${order.id}`);
      setOrder((p) => p && { ...p, ...o }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resync = async () => {
    try {
      const { data: o } = await api.post(`/payment-orders/${order.id}/sync`);
      setOrder((p) => p && { ...p, ...o }); load();
      if (o.value_divergence) toast.error("Divergência de valor detectada. Repasses retidos para conferência.");
      else if (o.fully_settled) toast.success("Tudo confirmado no Asaas: cobrança e repasses concluídos.");
      else toast.info("Status reconsultado no Asaas.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const expLeft = () => {
    if (!order?.expires_at) return null;
    const ms = new Date(order.expires_at).getTime() - now;
    if (ms <= 0) return "expirado";
    const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  if (!data) return null;
  const anyPayable = payable.length > 0;

  return (
    <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-6" data-testid="band-payments">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-head text-2xl font-semibold tracking-tight flex items-center gap-2"><Wallet className="h-6 w-6 text-accent" />Pagamentos da Banda</h2>
        {anyPayable && (
          <button data-testid="select-all-band" onClick={toggleAll} className="text-xs text-accent hover:underline flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />{selectedIds.length === payable.length ? "Limpar seleção" : "Selecionar todos"}
          </button>
        )}
      </div>

      {data.payment_mode === "protected" && (
        <div data-testid="custody-banner" className={`mb-4 rounded-sm border p-3 flex items-center justify-between gap-3 flex-wrap ${CUSTODY[data.custody_status]?.c || "border-white/10"}`}>
          <span className="flex items-center gap-2 text-sm font-medium">{CUSTODY[data.custody_status]?.icon}{CUSTODY[data.custody_status]?.t || "Pagamento protegido"}</span>
          {data.custody_status === "protegido" && (
            <Button data-testid="release-payment-btn" onClick={releasePayment} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm gap-2 h-9"><Banknote className="h-4 w-4" />Liberar pagamento</Button>
          )}
        </div>
      )}

      {data.musicians.length === 0 ? (
        <p className="text-sm text-zinc-500">Nenhum músico aprovado/confirmado ainda. Aprove candidatos para habilitar os pagamentos.</p>
      ) : (
        <div className="space-y-2">
          <div className="hidden md:grid grid-cols-[24px_1fr_120px_100px_120px_110px] gap-2 text-[10px] uppercase tracking-widest text-zinc-500 px-2">
            <span></span><span>Músico</span><span>Instrumento</span><span>Cachê</span><span>Recebimento</span><span>Pagamento</span>
          </div>
          {data.musicians.map((m) => {
            const done = m.payment_status !== "pendente";
            const meta = PAY_LABEL[m.payment_status] || PAY_LABEL.pendente;
            return (
              <div key={m.user_id} data-testid={`band-row-${m.user_id}`} className="bg-[#09090B] border border-white/[0.05] rounded-sm px-2 py-2.5 text-sm">
                {/* Mobile: stacked card */}
                <div className="flex items-start gap-2 md:hidden">
                  <input type="checkbox" data-testid={`band-check-m-${m.user_id}`} disabled={done} checked={!!sel[m.user_id]}
                    onChange={(e) => setSel({ ...sel, [m.user_id]: e.target.checked })} className="h-5 w-5 accent-[#FF4D00] disabled:opacity-30 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.stage_name}</div>
                    <div className="text-xs text-zinc-400 truncate">{m.instrument || "—"} · <span className={m.receipt_status === "Não cadastrado" ? "text-amber-400" : "text-emerald-400"}>{m.receipt_status}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-accent">{money(m.cache)}</div>
                    <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-sm border ${meta.c}`}>{meta.t}</span>
                  </div>
                </div>
                {/* Desktop: grid */}
                <div className="hidden md:grid grid-cols-[24px_1fr_120px_100px_120px_110px] items-center gap-2">
                  <input type="checkbox" data-testid={`band-check-${m.user_id}`} disabled={done} checked={!!sel[m.user_id]}
                    onChange={(e) => setSel({ ...sel, [m.user_id]: e.target.checked })} className="h-4 w-4 accent-[#FF4D00] disabled:opacity-30" />
                  <span className="font-medium truncate">{m.stage_name}</span>
                  <span className="text-zinc-400 truncate">{m.instrument || "—"}</span>
                  <span className="mono text-accent">{money(m.cache)}</span>
                  <span className={`text-xs ${m.receipt_status === "Não cadastrado" ? "text-amber-400" : "text-emerald-400"}`}>{m.receipt_status}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-sm border w-fit ${meta.c}`}>{meta.t}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="mt-5 bg-[#09090B] border border-[#FF4D00]/20 rounded-sm p-4" data-testid="band-summary">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Resumo</div>
          <div className="space-y-1 text-sm">
            <Row l={`Músicos selecionados`} v={selectedIds.length} mono />
            <Row l="Cachês" v={money(subtotal)} />
            <Row l={`Taxa Mí Chamou${fee.fee_type !== "fixed" ? ` (${fee.percentage}%)` : ""}`} v={money(feeTotal)} />
            {expressSurcharge > 0 && <Row l="⚡ Acréscimo Em cima da Hora" v={money(expressSurcharge)} />}
            <div className="border-t border-white/10 my-1" />
            <Row l="Total a pagar" v={money(total)} bold />
          </div>
          <Button data-testid="pay-band-btn" onClick={charge} disabled={busy} className="mt-3 w-full bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2">
            <QrCode className="h-4 w-4" />{busy ? "Gerando Pix..." : `Pagar ${selectedIds.length} músico(s) — ${money(total)}`}
          </Button>
          <p className="text-[11px] text-zinc-600 mt-2">Um único Pix cobre todos os selecionados. O repasse a cada músico é controlado individualmente pelo sistema.</p>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5" data-testid="band-history">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><History className="h-3.5 w-3.5" />Histórico de pagamentos</div>
          <div className="space-y-1.5">
            {history.map((h) => (
              <div key={h.id} data-testid={`band-order-${h.id}`} className="flex items-center justify-between bg-[#09090B] border border-white/[0.05] rounded-sm px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="mono text-accent">{money(h.total)}</span>
                  <span className="text-zinc-500"> · {h.recipients} músico(s) · {(h.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-sm border ${h.status === "PAID" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>{COLL_LABEL[h.status] || h.status}</span>
                  {h.status === "PAID"
                    ? <button data-testid={`receipt-btn-${h.id}`} onClick={() => downloadReceipt(h.id)} className="text-accent flex items-center gap-1 hover:underline"><Download className="h-3.5 w-3.5" />Recibo</button>
                    : <button onClick={() => api.get(`/payment-orders/${h.id}`).then((r) => setOrder(r.data))} className="text-zinc-400 hover:text-white flex items-center gap-1"><QrCode className="h-3.5 w-3.5" />Ver Pix</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!order} onOpenChange={(o) => { if (!o) { setOrder(null); load(); } }}>
        <DialogContent className="bg-[#121217] border-white/10" data-testid="band-pix-dialog">
          <DialogHeader><DialogTitle>Pagamento via Pix</DialogTitle></DialogHeader>
          {order && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold mono text-accent">{money(order.total)}</div>
                <div className="text-sm text-zinc-500">{order.event_name} · {order.recipients?.length} músico(s)</div>
              </div>
              {["PENDING", "PROCESSING"].includes(order.collection_status) && (
                <>
                  <div className="mx-auto h-52 w-52 bg-white rounded-sm flex items-center justify-center overflow-hidden">
                    {order.pix?.qr_code_base64
                      ? <img data-testid="band-pix-qr" src={`data:image/png;base64,${order.pix.qr_code_base64}`} alt="QR Code Pix" className="h-full w-full object-contain" />
                      : <QrCode className="h-40 w-40 text-black" />}
                  </div>
                  {order.pix?.copy_paste && (
                    <div className="bg-[#09090B] border border-white/10 rounded-sm p-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Pix copia e cola</div>
                      <div className="flex items-center gap-2"><code className="text-xs text-zinc-300 break-all flex-1">{order.pix.copy_paste}</code>
                        <button data-testid="band-copy-pix" onClick={() => copy(order.pix.copy_paste)} className="text-accent shrink-0"><Copy className="h-4 w-4" /></button></div>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 text-sm text-amber-400" data-testid="band-pix-waiting">
                    <span className="h-3 w-3 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
                    Aguardando pagamento{expLeft() ? ` · expira em ${expLeft()}` : ""}
                  </div>
                </>
              )}
              {order.collection_status === "PAID" && (
                <div className="space-y-3" data-testid="band-pix-paid">
                  {order.value_divergence ? (
                    <div className="text-center text-amber-400 font-semibold flex flex-col items-center gap-1" data-testid="band-pix-divergence">
                      <span className="flex items-center gap-2"><AlertTriangle className="h-6 w-6" />Pagamento com divergência de valor</span>
                      <span className="text-xs font-normal text-zinc-400">Recebido {money(order.value_received)} de {money(order.total)} esperado. Repasses retidos até conferência.</span>
                    </div>
                  ) : order.fully_settled ? (
                    <div className="text-center text-emerald-400 font-semibold flex items-center justify-center gap-2"><CheckCircle2 className="h-6 w-6" />Cobrança e repasses confirmados</div>
                  ) : (
                    <div className="text-center text-sky-400 font-semibold flex flex-col items-center gap-1">
                      <span className="flex items-center gap-2"><CheckCircle2 className="h-6 w-6" />Pagamento recebido</span>
                      <span className="text-xs font-normal text-zinc-400">Repasses aos músicos ainda não confirmados pelo Asaas.</span>
                    </div>
                  )}
                  <div className="bg-[#09090B] border border-white/10 rounded-sm divide-y divide-white/5" data-testid="payout-list">
                    {(order.recipients || []).map((r) => (
                      <div key={r.user_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="truncate">{r.stage_name}</span>
                        <span className="text-xs text-zinc-400 shrink-0">{money(r.net)} · <span className={r.payout_status === "PAID" ? "text-emerald-400" : r.payout_status === "FAILED" || r.payout_status === "NOT_ELIGIBLE" ? "text-red-400" : "text-sky-400"}>{PAYOUT_PT[r.payout_status] || r.payout_status}</span></span>
                      </div>
                    ))}
                  </div>
                  {order.is_organizer && !order.value_divergence && (order.recipients || []).some((r) => ["PENDING", "NOT_ELIGIBLE", "FAILED", "AGUARDANDO_SALDO"].includes(r.payout_status)) && (
                    <Button data-testid="run-payout-btn" onClick={runPayouts} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-sm gap-2"><Banknote className="h-4 w-4" />Repassar aos músicos (Pix)</Button>
                  )}
                  <Button data-testid="resync-asaas-btn" onClick={resync} variant="outline" className="w-full rounded-sm bg-transparent border-white/10 gap-2"><RefreshCw className="h-4 w-4" />Reconsultar Asaas</Button>
                  <p className="text-[11px] text-zinc-600 text-center">O repasse de cada músico é liberado após ele fazer check-in e check-out, e só é marcado como Recebido quando o Asaas confirma a transferência.</p>
                  <Button data-testid="download-receipt-btn" onClick={() => downloadReceipt(order.id)} variant="outline" className="w-full rounded-sm bg-transparent border-white/10 gap-2"><Download className="h-4 w-4" />Baixar recibo (PDF)</Button>
                </div>
              )}
              {["EXPIRED", "CANCELLED", "FAILED"].includes(order.collection_status) && (
                <div className="space-y-3">
                  <div className="text-center text-amber-400 flex items-center justify-center gap-2" data-testid="band-pix-expired"><AlertTriangle className="h-5 w-5" />{COLL_LABEL[order.collection_status]}</div>
                  <Button data-testid="regen-pix-btn" onClick={regenerate} className="w-full bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2"><RefreshCw className="h-4 w-4" />Gerar novo Pix</Button>
                </div>
              )}
              <div className="text-[11px] text-zinc-600 text-center">O status é confirmado pelo backend/Asaas — não é marcado como pago pelo clique.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ l, v, bold, mono }) => (
  <div className="flex justify-between"><span className="text-zinc-400">{l}</span><span className={`${mono ? "mono" : ""} ${bold ? "font-bold text-white" : "text-zinc-200"}`}>{v}</span></div>
);
